import { useSession } from '@/auth/useSession';
import { shouldRenderItem } from '@/components/should-render-item';
import { RegisteredToOptions } from '@/lib/routing';
import { cn } from '@/lib/tailwind-utils';
import { type LoggedInUser } from '@airreading/common/data';
import { Link } from '@tanstack/react-router';
import { ExternalLink, LucideIcon } from 'lucide-react';
import { FunctionComponent, SVGProps } from 'react';
import { isStudent as isAirStudent } from '@airreading/common';

export type ShouldRenderPredicate = (user: LoggedInUser) => boolean;

export type BaseNavLink = {
  label: string;
  iconComponent: LucideIcon | FunctionComponent<SVGProps<SVGSVGElement>>;
  expanded?: boolean;

  /**
   * predicate to determine if the link should be rendered.
   * if no predicate is provided, the link will be rendered.
   * if a single predicate is provided, it is used to determine if the link should be rendered.
   * if an array of predicates is provided, any predicate that returns true will cause the link to be rendered.
   * @param userToCheck
   * @returns
   */
  shouldRender?: ShouldRenderPredicate | ShouldRenderPredicate[];
};

export type InternalNavLink = BaseNavLink & {
  linkProps: RegisteredToOptions;
  fuzzyMatched?: boolean;
  exactMatched?: boolean;
  children?: ChildNavLinkDefinition[];
};

export type ExternalNavLink = BaseNavLink & {
  externalURL: string;
};

export type Divider = {
  isDivider: true;
  shouldRender?: ShouldRenderPredicate | ShouldRenderPredicate[];
};

export type NavLinkDefinition = InternalNavLink | ExternalNavLink | Divider;

export type ChildNavLinkDefinition = InternalNavLink & {
  expanded?: false;
  children?: never;
};

interface NavLinkProps {
  linkDefinition: InternalNavLink | ChildNavLinkDefinition;
  isChild?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}

export const NavLink = ({ linkDefinition, isChild, onNavigate, collapsed = false }: NavLinkProps) => {
  const { actingUser } = useSession();
  const {
    linkProps,
    label,
    iconComponent: Icon,
    exactMatched,
    fuzzyMatched,
    children = [],
    shouldRender,
  } = linkDefinition;
  const showHighlight = !children.length
    ? fuzzyMatched
    : exactMatched || (fuzzyMatched && !children.some((child) => child.fuzzyMatched));

  const isStudent = actingUser ? isAirStudent(actingUser) : false;

  const visibleItem = shouldRenderItem(actingUser, shouldRender);
  if (!visibleItem) return null;
  // Generate data-testid from label (convert to kebab-case)
  const testId = `nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <>
      <Link
        {...linkProps}
        data-testid={testId}
        onClick={onNavigate}
        title={collapsed ? label : undefined}
        aria-label={collapsed ? label : undefined}
      >
        <span
          className={cn(
            'group flex items-center py-2 text-xs gap-x-2',
            showHighlight
              ? 'bg-accent text-air-primary border-l-4 border-air-primary px-3 font-semibold'
              : 'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground px-4 font-medium',
            { 'pl-7': isChild && showHighlight && !collapsed },
            { 'pl-8': isChild && !showHighlight && !collapsed },
            { 'border-[#881b21]': isStudent && showHighlight },
            { 'bg-[#d23841]': isStudent && showHighlight },
            { 'hover:bg-[#d23841]': isStudent },
            { 'justify-center gap-x-0 px-2': collapsed },
          )}
        >
          <Icon
            className={cn('h-5 w-5 flex-shrink-0', {
              'stroke-white': isStudent,
            })}
          />
          {!collapsed && (
            <span
              className={cn({
                'text-white': isStudent,
              })}
            >
              {label}
            </span>
          )}
        </span>
      </Link>
      {!collapsed &&
        children?.map((child) => <NavLink key={child.label} linkDefinition={child} isChild onNavigate={onNavigate} />)}
    </>
  );
};

export interface ExternalNavLinkProps {
  linkDefinition: ExternalNavLink;
  isChild?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}

export const ExternalNavLink = ({ linkDefinition, isChild, onNavigate, collapsed = false }: ExternalNavLinkProps) => {
  const { actingUser } = useSession();
  const { externalURL, label, iconComponent: Icon, shouldRender } = linkDefinition;

  const isStudent = actingUser ? isAirStudent(actingUser) : false;

  const visibleItem = shouldRenderItem(actingUser, shouldRender);
  if (!visibleItem || !externalURL) return null;

  // Generate data-testid from label (convert to kebab-case)
  const testId = `nav-link-${label.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <a
      href={externalURL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'group flex items-center justify-between rounded-md px-4 py-2 text-xs font-medium hover:bg-accent hover:text-accent-foreground text-foreground gap-x-2',
        { 'pl-8': isChild && !collapsed },
        { 'hover:bg-slate-400': isStudent },
        { 'justify-center px-2': collapsed },
      )}
      data-testid={testId}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
    >
      <div className={cn('flex items-center gap-x-2', { 'gap-x-0': collapsed })}>
        <Icon className="h-5 w-5 flex-shrink-0" />
        {!collapsed && <span>{label}</span>}
      </div>
      {!collapsed && <ExternalLink className="h-4 w-4 text-muted-foreground" />}
    </a>
  );
};
